import { EMarket } from 'legacy/lut/configs/geo'
import {
  I621Form,
  IPartnerData,
  parseSectionSettings,
} from 'legacy/lut/features/partner360/configuration'
import { summaryProgramType } from 'legacy/lut/features/partner360/configuration/constants'
import { getFormedDeadlineSettings } from 'legacy/lut/services/partner360/configuration/helpers/getFormedDeadlineSettings'
import { parseProgramOfferings } from 'legacy/lut/services/partner360/configuration/helpers/parseProgramOfferings'

import { Region } from 'clickNgo/configs/geo'
import Logger from 'core/utils/Logger'
import { getOfferingActualsValueTypeDefault } from 'partner360/helpers/getOfferingActualsValueTypeDefault'
import {
  getOfferingActualsValueTypes,
  parseProgramOfferingsJson,
} from 'partner360/helpers/offerings/programOfferings'
import {
  getSectionSettingNumber,
  isSectionSettingOn,
} from 'partner360/helpers/sectionSettings/isSectionSettingOn'

import { parseSalesTypeButtons } from '../../../../features/partner360/configuration/helpers/parseSalesTypeButtons'

import { mapJbpSalesTypeToSalesType } from './mapJbpSalesTypeToSalesType'
import { mapPartner360ProgramTypes } from './mapPartner360ProgramTypes'

export const map621ToConfigurationData = (data: I621Form, programType?: string): IPartnerData => {
  const parsedData = parseSectionSettings(data.SectionSettings)

  const programTypesExtended = parseProgramOfferings(data.AllProgramOfferingsJson)
  const programOfferingsCatalog = parseProgramOfferingsJson(data.ProgramOfferingsJson)
  const offering = programTypesExtended.find((item) => item.Offering === programType)
  const availableProgramOfferings =
    data.ProgramOfferings?.split(',').filter((offering) => offering) ?? []
  const programTypes = mapPartner360ProgramTypes(programTypesExtended, availableProgramOfferings)
  const salesTypeButtons = parseSalesTypeButtons(data.SalesTypes).map(mapJbpSalesTypeToSalesType)

  let keyInitiativesPending = []
  try {
    keyInitiativesPending = JSON.parse(data.KIPending || '[]')
  } catch (e) {
    Logger.error(`KIPending: ${e}`)
    keyInitiativesPending = []
  }

  let profilesJson = []
  try {
    profilesJson = JSON.parse(data.ProfilesJson || '[]')
  } catch (e) {
    Logger.error(`ProfilesJson: ${e}`)
    profilesJson = []
  }

  let p360Profiles = []
  try {
    p360Profiles = JSON.parse(data.P360ProfilesJSON || '[]')
  } catch (e) {
    Logger.error(`P360ProfilesJSON: ${e}`)
    p360Profiles = []
  }

  return {
    rtm: data.RTM,
    fYear: data.FYear || 0,
    entryId: data.EntryId || 0,
    uid: data.UID,
    partnerId: data.PartnerId,
    partnerName: data.PartnerName,
    locationId: data.LocationId,
    partnerType: data.PartnerType,
    partnerFlags: data.PartnerFlags ?? '',

    programOfferings: availableProgramOfferings,
    programTypes: [...programTypes, summaryProgramType],
    deadlineSettings: getFormedDeadlineSettings(data.DeadlineSettings),
    programTypesExtended,
    programOfferingsCatalog,

    keyInitiativesPending,
    pbmHeaderKey: data.PBMHeaderKey,
    pbmTooltipKey: data.PBMTooltipKey,
    managerHeaderKey: data.ManagerHeaderKey,
    managerTooltipKey: data.ManagerTooltipKey,
    partnerHeaderKey: data.PartnerHeaderKey,
    partnerTooltipKey: data.PartnerTooltipKey,

    outOfScope: Boolean(data.OutOfScope),

    globalPartnerId: data.GlobalPartnerId,
    profiles: data.Profiles?.split(',').filter((it) => it.trim()),
    profilesJson,
    p360Profiles,
    amplifyTrack: data.AmplifyTrack,
    country: data.Country,
    geoMarket: data.GeoMarket as EMarket,
    geoMarketShort: data.GeoMarketShort?.trim() || null,
    geoRegion: data.GeoRegion as Region,

    dataInsight: {
      dataInsightFilename: data.DataInsightFilename || '',
      dataInsightFilenamePptx: data.DataInsightFilenamePptx || '',
      dataInsightHPLink: data.DataInsightHPLink || '',
      dataInsightHP2Link: data.DataInsightHP2Link || '',
      dataInsightId: data.DataInsightId || '',
      dataInsightPartnerLink: data.DataInsightPartnerLink || '',
    },

    currencyData: {
      localCurrency: data.LocalCurrency,
      defaultCurrency: data.DefaultCurrency,
      actualsMultiCurrency: data.ActualsMultiCurrency,
    },

    actualsData: {
      valueTypeDefault: getOfferingActualsValueTypeDefault(offering),
      valueTypes: getOfferingActualsValueTypes(programOfferingsCatalog, programType),
    },

    actualsDate: data.ActualsDate,

    actualsFromSellTo: data.ActualsFromSellTo,

    salesTypes: salesTypeButtons,

    files: {
      exportedFile: data.ExportedFile,
      exportedFileDaaS: data.ExportedFileDaaS,
      exportedFileDistributor: data.ExportedFileDistributor,
      exportedFileGeneral: data.ExportedFileGeneral,
      exportedFileLargeFormat: data.ExportedFileLargeFormat,
      exportedFileMPS: data.ExportedFileMPS,
      exportedFilePersonalSystemsSoftware: data.ExportedFilePersonalSystemsSoftware,
      exportedFileRetail: data.ExportedFileRetail,
      exportedFileCorePrint: data.ExportedFileCorePrint,
    },

    partnerAccess: {
      isMPS: data.IsMPS === 1,
      isMIF: data.MIF === 1,
      isGlobal: data.IsGlobal === 1,
      isHybrid: data.IsHybrid === 1,
      isCommercial: data.IsCommercial === 1,
      isRetail: data.IsRetail === 1,
      isDistributor: data.IsDistributor === 1,
      isRegionalDistributor: data.DistributorLevel === 'Regional',
      /** @deprecated */
      isPower: data.PartnerType === 'Power',
      isPowerElite: data.IsPowerElite === 1,
      isPowerEliteGlobal: data.IsPowerEliteGlobal === 1,
      isMasterDealer: data.MasterDealer === 1,
    },

    masterDealer: {
      masterDealerOfferings: (data.MasterDealerOfferings || '')
        .split(',')
        .filter((it) => it.trim()),
    },

    planning: {
      isBpsCategoryActive:
        parsedData['jbp-pc-bps-na'] === undefined
          ? true
          : getSectionSettingNumber(parsedData['jbp-pc-bps-na']) === 0,
      isCorePrintContractualPlanningActive: isSectionSettingOn(parsedData['coreprint-contractual']),
    },

    isTopPartner: data.IsTopPartner === 1,
    isWWTopPartner: data.IsWWTopPartner === 1,
    isMarketTopPartner: data.IsMarketTopPartner === 1,
    topPartnerInfo: data.TopPartnerInfo,
    isStrategicServicePartner: data.IsStrategicServicePartner === 1,
    strategicServicePartnerInfo: data.StrategicServicePartnerInfo,
    isInitializationRequired: data.InitRequired === 1,
    isIMPSServiceDelivery:
      typeof data.IMPSServiceDelivery === 'number' ? data.IMPSServiceDelivery === 1 : null,

    pyActuals: data.PYActuals,
    cyPlans: data.CYPlans,
  }
}
